
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import Filter from "./filter.vue";
import { debounce } from "lodash";
import { ClientNamePOSData } from "@/domain/entities/Partner";
import { CityData } from "@/domain/entities/Location";
import { OptionsClass } from "@/domain/entities/MainApp";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { PartnerController } from "@/app/ui/controllers/PartnerController";
import { BookingV3, ErrorBookingV3 } from "@/domain/entities/CreateBookingV3";
import { ClientData } from "@/domain/entities/BulkBooking";
import { parseZodError, validationCurrentIndex } from "../../utils";
import { clientScheme } from "./validation";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import { codePhoneCountry } from "@/app/ui/views/shipment/booking/booking-utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { createFocusTrap } from "focus-trap";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { updateContextField } from "@/feature-flags/unleash/index";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";

class Props {
  modelValue = prop<ClientData>({
    default: () => new ClientData(),
    required: true
  });
  error = prop<ErrorBookingV3>({
    default: () => new ErrorBookingV3(),
    required: true
  });
  currentIndex = prop<number>({
    default: 0,
    required: true
  });
  clientIndex = prop<number>({
    default: 1,
    required: true
  });
  isDisabled = prop<boolean>({
    default: false
  });
  updateClientIdFromShipment = prop<number>({
    default: 0,
    required: true
  });
}

@Options({
  components: {
    Filter,
    Skeleton
  },
  watch: {
    originValue: {
      handler(val: OptionsClass) {
        const oldFilter = this.filterValues.filter(
          (f: OptionsClass) => f.meta?.type !== "origin"
        );
        if (val.name) {
          this.filterValues = [...oldFilter, val];
        } else {
          this.filterValues = [...oldFilter];
        }
      },
      deep: true
    },
    deliveryServiceValues: {
      handler(val: OptionsClass[]) {
        const oldFilter = this.filterValues.filter(
          (f: OptionsClass) => f.meta?.type !== "delivery"
        );
        this.filterValues = [...oldFilter, ...val];
      },
      deep: true
    },
    currentIndex(index: number) {
      if (validationCurrentIndex(this.clientIndex, index)) {
        this.onValidateClient(this.clientValue);
      }
    },
    updateClientIdFromShipment: {
      async handler(val: number) {
        if (!this.flagEnableHandleSelectClientIdFromShipment) return;
        this.selectClientFromShipment(val);
      },
      immediate: true
    }
  }
})
export default class FilterClient extends Vue.with(Props) {
  filterValues: OptionsClass[] = [];
  originsData: CityData[] = [];
  originsPage = 1;
  originValue = new OptionsClass();
  originLoading = false;
  originSearchValue = "";

  deliveryServiceValues: OptionsClass[] = [];

  isFilterOpen = false;

  clientsData: ClientNamePOSData[] = [];
  clientsDataPage = 1;
  clientValue = new OptionsClass();
  clientLoading = false;
  clientSearchValue = "";
  openClientModal = false;
  focusTrap: any = null;
  selectedClientCode: string | null = null;

  async mounted() {
    if (this.modelValue.clientName) {
      const clientAlias = this.modelValue.clientAlias;
      this.clientValue = new OptionsClass({
        name: this.modelValue.clientName,
        value: this.modelValue.clientId.toString(),
        meta: { type: "client" },
        label: `${clientAlias ? clientAlias + " - " : ""} ${
          this.modelValue.clientName
        }`
      });
    }
    await this.$nextTick();
    (this.$refs.filterClient as any).$refs.filterClientButton.focus();
  }

  unmounted() {
    updateContextField("input_client_country", "");
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  get assigned3lc() {
    const assigned3lc = AccountController.accountData.assigned3lc || "ALL";
    return assigned3lc.toUpperCase();
  }

  get showButtonResetFilter() {
    return this.filterValues.length;
  }

  get cityOptions() {
    const options = [
      ...this.originsData.map(
        (origin) =>
          new OptionsClass({
            name: origin.codeNameCity,
            label: origin.codeNameCity,
            value: origin.code,
            meta: { type: "origin" }
          })
      )
    ];

    if (!this.originSearchValue && this.assigned3lc.includes("ALL")) {
      const label = this.accountIsNonForeign ? "Semua" : "All";
      options.unshift(
        new OptionsClass({
          name: label,
          label: label,
          value: "0",
          meta: { type: "origin" }
        })
      );
    }

    return options;
  }

  get clientOptions() {
    return this.clientsData.map((client) => {
      const clientAlias = client.clientAlias;
      return new OptionsClass({
        name: client.clientName,
        value: client.clientId.toString(),
        meta: { type: "client" },
        label: `${clientAlias ? clientAlias + " - " : ""}${client.clientName}`
      });
    });
  }

  get deliveryServiceOptions() {
    return [
      new OptionsClass({
        name: "COD",
        label: "COD",
        value: "cod",
        meta: { type: "delivery" }
      }),
      new OptionsClass({
        name: "DFOD",
        label: "DFOD",
        value: "dfod",
        meta: { type: "delivery" }
      }),
      new OptionsClass({
        name: "DO",
        label: "DO",
        value: "do",
        meta: { type: "delivery" }
      }),
      new OptionsClass({
        name: "Non-COD",
        label: "Non-COD",
        value: "non_cod",
        meta: { type: "delivery" }
      })
    ];
  }

  get isFiltered() {
    return (
      this.clientSearchValue ||
      this.originValue.name ||
      this.deliveryServiceValues.length
    );
  }

  async fetchOrigins({ search = "", firstFetch = true }) {
    this.originSearchValue = search;
    try {
      if (firstFetch) {
        this.originsData = [];
        this.originsPage = 1;
      }
      this.originLoading = true;
      const resp = await LocationController.getCityList({
        page: this.originsPage,
        limit: 10,
        search,
        status: "",
        newEndpoint: true,
        code: this.assigned3lc !== "ALL" ? this.assigned3lc : ""
      });

      this.originsData.push(...resp.cityData);
    } catch (error) {
      this.originsData = [];
      this.originsPage = 1;
    } finally {
      this.originLoading = false;
    }
  }

  fetchOriginsDebounced = debounce(async (search) => {
    this.fetchOrigins({ search, firstFetch: true });
  }, 500);

  async fetchOriginsInfiniteScroll(search = "") {
    this.originsPage++;
    await this.fetchOrigins({ search, firstFetch: false });
  }

  deleteOriginValue() {
    this.originValue = new OptionsClass();
  }

  deleteClientValue() {
    this.clientValue = new OptionsClass();
    this.onValidateClient(this.clientValue);
    this.$emit("update:modelValue", new ClientData());
  }

  deleteDeliveryService(index = 0) {
    this.deliveryServiceValues.splice(index, 1);
  }

  resetFilter() {
    this.deleteClientValue();
    this.deliveryServiceValues = [];
    this.filterValues = [];
    this.clientSearchValue = "";
    this.originValue = new OptionsClass();
    this.$emit("select");
  }

  async fetchClients({ search = "", isFirstFetch = true }) {
    try {
      if (isFirstFetch) {
        this.clientsData = [];
        this.clientsDataPage = 1;
      }
      this.clientLoading = true;
      const originValue = this.originValue.value;
      const resp = await PartnerController.getClientNamePOS({
        page: this.clientsDataPage,
        packageType: this.deliveryServiceValues
          .map((delivery) => delivery.value)
          .join(","),
        isApiCbp: false,
        isBranch: true,
        isDirectory: false,
        limit: 10,
        cityCode: originValue === "0" ? "" : originValue,
        search: search
      });
      if (!resp.data.length && !isFirstFetch) return;
      this.clientsDataPage++;
      this.clientsData.push(...resp.data);
      this.$emit("result", this.clientsData);
    } catch (error) {
      this.clientsData = [];
      this.clientsDataPage = 1;
    } finally {
      this.clientLoading = false;
    }

    this.$emit("update:currentIndex", this.clientIndex);
  }

  fetchClientsDebounced = debounce(async (search) => {
    this.fetchClients({ search, isFirstFetch: true });
  }, 500);

  fetchClientsInfiniteScroll(search = "") {
    this.clientsDataPage++;
    this.fetchClients({ search, isFirstFetch: false });
  }

  showClientListModal() {
    this.openClientModal = true;
    this.$nextTick().then(() => {
      const modal = document.querySelector(
        ".client-filter-modal"
      ) as HTMLElement;
      if (modal) {
        // kalau trap belum ada, bikin
        if (!this.focusTrap) {
          this.focusTrap = createFocusTrap(modal, {
            escapeDeactivates: true, // ⎯ close dengan Esc
            clickOutsideDeactivates: true, // ⎯ click luar = close
            fallbackFocus: modal,
            onDeactivate: () => {
              this.openClientModal = false;
              this.focusTrap = null;
            }
          });
        }
        this.focusTrap.activate();
      }
    });
  }

  closeModal() {
    this.openClientModal = false;
    this.focusTrap?.deactivate(); // ⬅️ release trap
  }

  setClientsData(data: ClientNamePOSData[]) {
    this.clientsData = data;
  }

  onUpdateClient(val: OptionsClass) {
    if (val.name) {
      this.handleClientSelect(val);
    } else {
      this.resetClientSelection();
    }

    this.onValidateClient(this.clientValue);
  }

  onSelectClient(client: ClientNamePOSData) {
    this.closeModal();
    this.removeClientFromFilter(this.clientValue.name);
    const clientAlias = client.clientAlias;

    this.clientValue = new OptionsClass({
      name: client.clientName,
      value: client.clientId.toString(),
      meta: { type: "client" },
      label: `${clientAlias ? clientAlias + " - " : ""}${client.clientName}`
    });

    this.clientSearchValue = client.clientName;
    this.filterValues.push(this.clientValue);

    this.onValidateClient(this.clientValue);
    this.$emit("select", client);
    this.emitClientData(client);
  }

  private handleClientSelect(val: OptionsClass) {
    this.removeClientTypeFromFilter();

    this.clientSearchValue = val.name;
    this.filterValues.push(val);

    const selectedClient = this.clientsData.find(
      (client) => client.clientName === val.name
    );
    this.$emit("select", selectedClient);

    const clientData = this.clientsData.find(
      (client) => client.clientId.toString() === val.value
    );
    if (clientData) {
      this.emitClientData(clientData);
    }
  }

  private resetClientSelection() {
    this.removeClientTypeFromFilter();
    this.clientSearchValue = "";
    this.$emit("select");
    this.$emit("update:modelValue", new ClientData());
    this.$emit("client", new DetailClientManagement());
  }

  private removeClientTypeFromFilter() {
    this.filterValues = this.filterValues.filter(
      (filter: OptionsClass) => filter?.meta?.type !== "client"
    );
  }

  private removeClientFromFilter(clientName: string) {
    this.filterValues = this.filterValues.filter(
      (filter) => filter.name !== clientName
    );
  }

  private async getClientDetail(id: number) {
    const resp = (await ClientManagementController.getDetailV2(
      id
    )) as DetailClientManagement;
    if (AccountController.accountData.isInternalAccount) {
      await LocationController.getDetailDistrict({
        id: resp.clientDistrict.id
      });
    }
    this.$emit("update:sender", resp.clientName);
    this.$emit("update:phoneSender", codePhoneCountry(resp.clientPhone).number);
    this.$emit(
      "update:phoneCodeSender",
      codePhoneCountry(resp.clientPhone).code
    );
    this.$emit("update:addressSender", resp.clientAddress);
    this.$emit("client", resp);
    return resp;
  }

  private async emitClientData(client: ClientNamePOSData) {
    const resp = await this.getClientDetail(client.clientId);
    updateContextField("input_client_country", resp?.clientCountry || "");
    this.$emit(
      "update:modelValue",
      new ClientData({
        id: client.clientId,
        name: client.clientName,
        clientCode: client.clientCode,
        clientId: client.clientId,
        clientName: client.clientName,
        clientOriginCity: client.clientOriginCity,
        clientPackageType: client.clientPackageType,
        isCOD: client.clientIsCOD,
        isDO: client.clientIsDO,
        clientAlias: client.clientAlias,
        clientCategory: client.clientCategory,
        clientCodConfigAmountData: client.clientCodConfigAmount,
        clientCountry: resp?.clientCountry || "",
        isFtz:
          typeof resp.isFtz === "string" ? resp.isFtz === "yes" : resp.isFtz
      })
    );
  }

  onValidateClient(_clientvalue: OptionsClass) {
    // filter client is no longer mandatory
    // const message = parseZodError(clientScheme, clientvalue.value);
    // this.error.client = message;
  }

  deleteFilter(option: OptionsClass) {
    const obj = {
      origin: () => {
        this.originValue = new OptionsClass({ meta: { type: "origin" } });
      },
      client: () => {
        this.resetClientSelection();
        this.clientValue = new OptionsClass();
        this.$emit("update:modelValue", new ClientData());
        this.onValidateClient(this.clientValue);
        this.filterValues = this.filterValues.filter(
          (filter) => filter.name != option.name
        );
      },
      delivery: () => {
        const index = this.deliveryServiceValues.findIndex(
          (delivery) => delivery.name === option.name
        );
        this.deleteDeliveryService(index);
      }
    } as any;
    obj[option?.meta?.type]();
  }

  onModalEnter() {
    this.fetchClients({ search: this.clientSearchValue, isFirstFetch: true });
    this.$nextTick(() => {
      // const el = this.$el as HTMLElement;
      // const tableContainer = el.querySelector(".bg-white.overflow-auto") as HTMLElement | null;
      // tableContainer?.focus();

      const filterClientSearchEl = document.getElementById(
        "filter-client-input-search"
      );
      if (filterClientSearchEl) filterClientSearchEl.focus();
    });
  }

  onFocusRow(e: FocusEvent, client: any) {
    this.selectedClientCode = client.clientCode;
    const input = e.target as HTMLInputElement;
    input
      .closest("tr")
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  fetchNew({ target: { scrollTop } }: any) {
    if (scrollTop > 0) {
      this.fetchClients({
        search: this.clientSearchValue,
        isFirstFetch: false
      });
    }
  }
  async onCloseModal() {
    this.openClientModal = false;
    await this.$nextTick();
    const openListBtn = document.getElementById("filter-client-open-list-btn");
    if (this.focusTrap) {
      this.focusTrap.deactivate();
      this.focusTrap = null;
    }
    if (openListBtn) openListBtn.focus();
  }

  async selectClientFromShipment(val: number) {
    if(!val) return;

    this.deleteClientValue();
    this.deliveryServiceValues = [];
    this.filterValues = [];
    this.clientSearchValue = "";
    this.originValue = new OptionsClass();

    const resp = await this.getClientDetail(val);
    this.generateClientDataAsSelectedClientData(resp);
  }

  generateClientDataAsSelectedClientData(resp: any) {
    // updateClientIdFromShipment:
    this.$emit(
      "update:modelValueAsSelectedClientData",
      new ClientData({
        id: resp.id,
        name: resp?.clientName,
        clientCode: resp?.clientCode,
        clientId: resp?.id,
        clientName: resp?.clientName,
        clientOriginCity: `${resp?.clientCity.cityCode} - ${resp?.clientCity.name}`,
        // clientPackageType: resp?.clientPackageType,
        isCOD: resp?.clientIsCOD,
        isDO: resp?.clientIsDO,
        clientAlias: resp?.clientAlias,
        clientCategory: resp?.clientCategory,
        clientCodConfigAmountData: resp?.clientCodConfigAmount,
        clientCountry: resp?.clientCountry || "",
        isFtz:
          typeof resp?.isFtz === "string" ? resp.isFtz === "yes" : resp?.isFtz,
        // clientPartner: client.clientPartner
      })
    );
  }

  get flagEnableHandleSelectClientIdFromShipment() {
    return (
      AccountController.accountData.account_type_detail.type === "pos" &&
      FlagsPermissionBooking.permission_generate_client_data_from_shipment_id.isEnabled()
    );
  }
}
